import {
	faBuilding,
	faCheck,
	faChevronRight,
	faFileInvoice,
	faRulerCombined,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	canNavigateToAufmass,
	getAufmassWorkflowState,
	getInvoiceWorkflowState,
	getSupplierInfoWorkflowState,
} from "../utils/invoice-workflow-utils";

const InvoiceWorkflowSteps = ({
	data,
	projectCan,
	onInvoiceClick,
	onAufmassClick,
	onSupplierInfoClick,
}) => {
	const { t } = useTranslation();

	// A node is reachable only when (a) the workflow state allows it AND (b) the
	// member holds the matching invoice permission - mirroring the row action
	// menu so these quick-nav nodes can't bypass the schema. The aufmass and
	// supplier-info nodes open their editors → need `manageAufmass` /
	// `manageSupplierInfo`; the invoice node is a read → `view`.
	const steps = useMemo(() => {
		const allows = (action) =>
			!projectCan || projectCan("invoice", action);
		return [
			{
				key: "invoice",
				icon: faFileInvoice,
				state: getInvoiceWorkflowState(data),
				navigable: true,
				allowed: allows("view"),
				onClick: onInvoiceClick,
			},
			{
				key: "aufmass",
				icon: faRulerCombined,
				state: getAufmassWorkflowState(data),
				navigable: canNavigateToAufmass(data),
				allowed: allows("manageAufmass"),
				onClick: onAufmassClick,
			},
			{
				key: "supplier-info",
				icon: faBuilding,
				state: getSupplierInfoWorkflowState(data),
				navigable: !!data?.hasSupplierInfo,
				allowed: allows("manageSupplierInfo"),
				onClick: onSupplierInfoClick,
			},
		].map((step) => ({
			...step,
			canNavigate: step.navigable && step.allowed,
			permissionDenied: step.navigable && !step.allowed,
		}));
	}, [data, projectCan, onInvoiceClick, onAufmassClick, onSupplierInfoClick]);

	const tooltipFor = useCallback(
		(step) => {
			const label = t(`invoice.step.${step.key}`);
			const status = t(`invoice.workflow-state.${step.state}`);
			const base = `${label} (${status})`;
			if (step.permissionDenied) {
				return `${base} - ${t("common.no-permission")}`;
			}
			return step.canNavigate
				? `${base} - ${t("invoice.workflow-click-to-open")}`
				: base;
		},
		[t],
	);

	const handleStepClick = useCallback(
		(event, step) => {
			event.stopPropagation();
			if (!step.canNavigate || !step.onClick || !data) return;
			step.onClick(data);
		},
		[data],
	);

	return (
		<div
			className="pi-workflow-track"
			role="list"
			aria-label={t("invoice.workflow-progress")}
		>
			{steps.map((step, index) => {
				const nodeClassName = [
					"pi-workflow-node",
					`pi-workflow-node--${step.state}`,
					step.canNavigate ? "pi-workflow-node--clickable" : "",
				]
					.filter(Boolean)
					.join(" ");

				const nodeContent = (
					<>
						<FontAwesomeIcon
							icon={step.icon}
							className="pi-workflow-node__icon"
						/>
						{step.state === "done" && (
							<FontAwesomeIcon
								icon={faCheck}
								className="pi-workflow-node__check"
								aria-hidden
							/>
						)}
					</>
				);

				return (
					<span
						key={step.key}
						className="pi-workflow-track__segment"
						role="listitem"
					>
						{step.canNavigate ? (
							<button
								type="button"
								className={nodeClassName}
								title={tooltipFor(step)}
								aria-label={tooltipFor(step)}
								onClick={(event) => handleStepClick(event, step)}
							>
								{nodeContent}
							</button>
						) : (
							<span
								className={nodeClassName}
								title={tooltipFor(step)}
							>
								{nodeContent}
							</span>
						)}
						{index < steps.length - 1 && (
							<FontAwesomeIcon
								icon={faChevronRight}
								className="pi-workflow-track__sep"
								aria-hidden
							/>
						)}
					</span>
				);
			})}
		</div>
	);
};

export default memo(InvoiceWorkflowSteps);
